/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React from "react";
import { Link } from "react-router-dom";
import cn from "classnames";
import s from "./Button.module.css";
import { InteractiveElementsOptionalProps } from "./interface";

interface LinkItemPropsInterface
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  slug: string | (() => string);
  className?: string;
  isActive?: boolean;
  isExternal?: true;
  disabled?: boolean;
  children: React.ReactNode | string;
}

const LinkItem = ({
  children,
  slug = "",
  className,
  isActive,
  isExternal,
  disabled,
  variant,
  size,
  shape,
  ...rest
}: LinkItemPropsInterface & InteractiveElementsOptionalProps) => {
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) return e.preventDefault();
    e.currentTarget.blur();
    e.stopPropagation();

    if (rest.onClick) {
      rest.onClick(e);
    }
  };

  const Tag: typeof Link | string = isExternal ? "a" : Link;

  const attributes = {
    to: "",
  };

  if (isExternal) {
    Object.assign(attributes, {
      href: slug || "",
      target: "_blank",
      rel: "noopener noreferrer",
    });

    delete attributes.to;
  } else {
    let to = slug || "";
    if (typeof slug === "function") {
      to = slug();
    }

    Object.assign(attributes, {
      to,
    });
  }

  const rootClass = cn(
    s.linkItem,
    s.root,
    variant && s[variant],
    size && [s[size]],
    shape && [s[shape]],
    { [s.active]: isActive },
    { "cursor-default pointer-events-none": disabled },
    disabled && s.disabled,
    className,
  );

  return (
    <Tag className={rootClass} onClick={onClick} {...attributes} {...rest}>
      {children}
    </Tag>
  );
};

export default LinkItem;
